import { extractNodesFromHtml } from "components/Resume/ResumePDF/common";
import {
  Resume,
  ResumeAward,
  ResumeCertificate,
  ResumeEducation,
  ResumeLanguage,
  ResumeProject,
  ResumeWorkExperience,
  ResumeCustom,
} from "lib/redux/types";
import {
  DescriptionWithId,
  ResumeAwardRequest,
  ResumeCertificateRequest,
  ResumeCustomRequest,
  ResumeEducationRequest,
  ResumeLanguageRequest,
  ResumeProjectRequest,
  ResumeScoreRequestBody,
  ResumeWorkExperienceRequest,
} from "./request-body-types";

type Section =
  | ResumeWorkExperience
  | ResumeProject
  | ResumeEducation
  | ResumeCertificate
  | ResumeAward
  | ResumeLanguage
  | ResumeCustom;

export const createResumeScoringRequestBody = (resume: Resume) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;

  let globalIdCounter = 1;
  let globalBlockIdCounter = 1;

  // Function to generate descriptions with globally unique IDs
  const generateDescriptionsWithIds = (
    descriptions: string[]
  ): DescriptionWithId[] => {
    return descriptions.map((description) => {
      return {
        statement: description,
        id: globalIdCounter++,
      };
    });
  };

  // Function to process descriptions for a section
  const processDescriptions = (descriptions: string[]) => {
    const textDescriptions = getDescriptionsArray(descriptions);
    return generateDescriptionsWithIds(
      textDescriptions.filter((desc): desc is string => desc !== null)
    );
  };

  // Function to replace descriptions in a section with new descriptions containing IDs
  const replaceDescriptionsInSection = (section: Section) => {
    if (section.descriptions && Array.isArray(section.descriptions)) {
      return {
        ...section,
        descriptions: processDescriptions(section.descriptions as string[]),
        id: globalBlockIdCounter++,
      };
    }
    return { ...section, id: globalBlockIdCounter++ };
  };

  const processSectionArray = (sections: Section[]) => {
    return sections?.map(replaceDescriptionsInSection) ?? [];
  };

  // Type-safe section processors
  const processWorkExperiences = (
    experiences: ResumeWorkExperience[]
  ): ResumeWorkExperienceRequest[] => {
    return experiences.map((exp) => ({
      ...exp,
      id: globalBlockIdCounter++,
      descriptions: processDescriptions(exp.descriptions as string[]),
    }));
  };

  const processEducations = (
    edus: ResumeEducation[]
  ): ResumeEducationRequest[] => {
    return edus.map((edu) => ({
      ...edu,
      id: globalBlockIdCounter++,
      descriptions: processDescriptions(edu.descriptions as string[]),
    }));
  };

  const processProjects = (projs: ResumeProject[]): ResumeProjectRequest[] => {
    return projs.map((proj) => ({
      ...proj,
      id: globalBlockIdCounter++,
      descriptions: processDescriptions(proj.descriptions as string[]),
    }));
  };

  const processCertifications = (
    certs: ResumeCertificate[]
  ): ResumeCertificateRequest[] => {
    return certs.map((cert) => ({
      ...cert,
      id: globalBlockIdCounter++,
      descriptions: processDescriptions(cert.descriptions as string[]),
    }));
  };

  const processLanguages = (
    langs: ResumeLanguage[]
  ): ResumeLanguageRequest[] => {
    return langs.map((lang) => ({
      ...lang,
      id: globalBlockIdCounter++,
      descriptions: processDescriptions(lang.descriptions as string[]),
    }));
  };

  const processAwards = (awds: ResumeAward[]): ResumeAwardRequest[] => {
    return awds.map((awd) => ({
      ...awd,
      id: globalBlockIdCounter++,
      descriptions: processDescriptions(awd.descriptions as string[]),
    }));
  };

  const processProfileSummary = (
    summary: string | null
  ): string | DescriptionWithId => {
    if (!summary)
      return {
        statement: "",
        id: globalIdCounter++,
      };
    return summary.trim()
      ? { statement: summary, id: globalIdCounter++ }
      : { statement: "", id: globalIdCounter++ };
  };

  const processCustom = (cust: ResumeCustom): ResumeCustomRequest => {
    return {
      ...cust,
      id: globalBlockIdCounter++,
      descriptions: processDescriptions(cust.descriptions),
    };
  };

  const processedResume: ResumeScoreRequestBody = {
    ...resume,
    profile: {
      ...profile,
      summary: processProfileSummary(profile.summary),
      id: globalBlockIdCounter++,
    },
    workExperiences: processWorkExperiences(workExperiences),
    educations: processEducations(educations),
    projects: processProjects(projects),
    skills: {
      id: globalBlockIdCounter++,
      featuredSkills: skills.featuredSkills,
      descriptions: processDescriptions(skills.descriptions),
    },
    certifications: processCertifications(certifications),
    languages: processLanguages(languages),
    awards: processAwards(awards),
    custom: processCustom(custom),
  };

  return processedResume;
};

const getDescriptionsArray = (descriptions: string[]) => {
  const descriptionNodes = extractNodesFromHtml(descriptions?.join(""));
  const textArray = getTextArrayFromDescriptionNodes(descriptionNodes);
  return textArray;
};

const getTextArrayFromDescriptionNodes = (descriptionNodes: Node[]) => {
  const textArray = descriptionNodes.flatMap((node) => {
    const element = node as Element;
    if (element.nodeName.toLowerCase() === "p") {
      return [element.textContent];
    } else if (element.nodeName.toLowerCase() === "ol") {
      const liNodes = Array.from(element.children);
      return liNodes.map((liNode) => {
        const liElement = liNode as Element;
        return liElement.textContent;
      });
    } else {
      return [];
    }
  });

  return textArray;
};

// Process each section
//   const processedResume = {
//     ...resume,
//     profile: {
//       ...profile,
//       summary: processProfileSummary(profile.summary),
//       id: globalBlockIdCounter++,
//     },
//     workExperiences: processSectionArray(workExperiences),
//     educations: processSectionArray(educations),
//     projects: processSectionArray(projects),
//     skills: {
//       id: globalBlockIdCounter++,
//       featuredSkills: skills.featuredSkills,
//       descriptions: processDescriptions(skills.descriptions),
//     },
//     certifications: processSectionArray(certifications),
//     languages: processSectionArray(languages),
//     awards: processSectionArray(awards),
//     custom: {
//       ...custom,
//       descriptions: processDescriptions(custom.descriptions),
//       id: globalBlockIdCounter++,
//     },
//   };
